import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import WebsiteForm from '../components/ui/WebsiteForm';
import { Website } from '../types';
import { websiteService } from '../services/api';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

// Donnée d'exemple pour représenter un site web
const mockWebsite: Website = {
  id: '1',
  clientName: 'Exemple Client',
  url: 'https://exemple-site.com/',
  platform: 'WordPress',
  creationDate: '2023-01-01',
  anniversaryDate: '2023-01-01',
  hostingType: 'VPS',
  hostingProvider: 'Hébergeur Example',
  ipAddress: '192.168.1.1',
  serverConfig: 'PHP 8.1',
  sslExpiration: '2025-01-01',
  contractType: 'Maintenance Annuelle',
  amount: 300,
  lastBillingDate: '2024-01-01',
  nextBillingDate: '2025-01-01',
  paymentStatus: 'Payé',
  domain: 'exemple-site.com',
  domainExpiration: '2025-01-01',
  registrar: 'Registrar Example',
  dnsConfig: {
    cloudflareProxy: false,
    cacheHeaders: false
  },
  emailType: 'smtp',
  emailNote: '',
  smtpServer: 'mail.exemple-site.com',
  smtpEmail: 'contact@exemple-site.com',
  smtpPassword: '',
  smtpPort: 587,
  encryption: 'TLS',
  postalHttpUrl: '',
  postalHttpApiKey: '',
  postalHttpSenderEmail: '',
  captchaType: 'reCAPTCHA',
  bucketName: 'exemple-bucket',
  status: 'active',
  backupType: 'minio',
  backupNote: '',
  backupFrequency: 'Hebdomadaire',
  maintenanceComments: 'Exemple de commentaire de maintenance',
  cookiesManagement: 'cookiesyes',
  primaryContact: 'Jean Dupont',
  contactEmail: 'jean@exemple.com',
  contactPhone: '01 23 45 67 89',
  comments: 'Exemple de commentaire',
  // Champs pour la compatibilité
  emailAddresses: ['contact@exemple-site.com'],
  authType: 'reCAPTCHA'
};

const WebsiteEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [website, setWebsite] = useState<Website | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les données du site web depuis l'API
  useEffect(() => {
    const fetchWebsite = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await websiteService.getById(id);
        setWebsite(data);
        setError(null);
      } catch (err: any) {
        console.error('Erreur lors du chargement du site :', err);
        setError(err.response?.data?.message || 'Impossible de charger les données du site. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWebsite();
  }, [id]);

  const handleSubmit = async (updatedWebsite: Website) => {
    if (!id) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Appel de l'API pour mettre à jour le site
      await websiteService.update(id, updatedWebsite);
      // Rediriger vers la page de détail après mise à jour
      navigate(`/sites/${id}`);
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour du site :', err);
      setError(err.response?.data?.message || 'Une erreur est survenue lors de la mise à jour du site. Veuillez réessayer.');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/sites/${id}`);
  };

  // Si le site est en cours de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Si une erreur est survenue lors du chargement
  if (error && !website) {
    return (
      <div className="bg-red-100 dark:bg-red-900 p-4 rounded-md text-red-800 dark:text-red-200">
        <p>{error}</p>
        <button 
          onClick={() => navigate('/sites')} 
          className="mt-4 btn-primary"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  // Si le site n'existe pas
  if (!website) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Site non trouvé</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Le site web que vous recherchez n'existe pas ou a été supprimé.</p>
        <Link to="/sites" className="btn-primary">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <Link to={`/sites/${id}`} className="text-primary-600 dark:text-primary-400 hover:underline flex items-center mb-2">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour aux détails
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Modifier {website.clientName}</h1>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 dark:bg-red-900 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      <WebsiteForm 
        website={website}
        onSubmit={handleSubmit} 
        onCancel={handleCancel}
        isSubmitting={submitting}
      />
    </div>
  );
};

export default WebsiteEdit; 